import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/useNotifications";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { IconButton } from "@a2z/ui";
import type { NotificationBellProps, PanelPosition } from "../types/notification-bell.types";

function formatRelativeTime(date: Date, locale: string): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) {
    return locale === "ar" ? "الآن" : "Just now";
  }
  if (diffMinutes < 60) {
    return locale === "ar" ? `منذ ${diffMinutes} د` : `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return locale === "ar" ? `منذ ${diffHours} س` : `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return locale === "ar" ? `منذ ${diffDays} ي` : `${diffDays}d ago`;
  }
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}


export function NotificationBell({ adminId, locale }: NotificationBellProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotifications(adminId);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<PanelPosition>({ top: 0, left: 0 });
  const isRtl = locale === "ar";
  // Clamp to the viewport so the panel never overflows on <=336px screens.
  const panelWidth = Math.min(
    320,
    (typeof window !== "undefined" ? window.innerWidth : 320) - 16,
  );

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const top = rect.bottom + 8;
    const left = isRtl
      ? Math.min(rect.right, window.innerWidth - 8) - panelWidth
      : Math.max(8, rect.left);
    setPosition({ top, left: Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8)) });
  }, [isRtl, panelWidth]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, updatePosition]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        ariaLabel={t(keys.admin.notificationsTitle)}
        variant="ghost"
        size="sm"
        shape="round"
        className="relative text-[color:var(--text-muted)]"
        icon={
          <>
            <BellIcon />
            {unreadCount > 0 ? (
              <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--error-500)] px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
          </>
        }
      />

      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[9999] overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-lg"
              style={{
                top: position.top,
                left: position.left,
                width: panelWidth,
              }}
            >
              <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-4 py-3">
                <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
                  {t(keys.admin.notificationsTitle)}
                </h3>
                {unreadCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => void markAllAsRead()}
                    className="rounded px-1 py-2 text-xs font-medium text-[color:var(--primary-500)] hover:text-[color:var(--primary-600)]"
                  >
                    {t(keys.admin.notificationsMarkAllRead)}
                  </button>
                ) : null}
              </div>

              <div className="no-scrollbar max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-[color:var(--text-muted)]">
                    {t(keys.admin.notificationsEmpty)}
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => {
                        void markAsRead(notification.id);
                        setIsOpen(false);
                        const storeId = notification.data?.storeId;
                        const orderId = notification.data?.orderId;
                        if (storeId && orderId) {
                          navigate(`/${locale}/admin/stores/${storeId}/orders/${orderId}`);
                        } else if (storeId) {
                          navigate(`/${locale}/admin/stores/${storeId}`);
                        }
                      }}
                      className={`flex w-full cursor-pointer flex-col gap-1 border-b border-[color:var(--border-light)] px-4 py-3 text-start transition last:border-b-0 hover:bg-[color:var(--surface-alt)] ${
                        !notification.read ? "bg-[color:var(--primary-50)]" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-[color:var(--text-primary)]">
                          {notification.title}
                        </span>
                        {!notification.read ? (
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[color:var(--primary-500)]" />
                        ) : null}
                      </div>
                      <span className="text-xs text-[color:var(--text-muted)]">
                        {notification.body}
                      </span>
                      <span className="text-[11px] text-[color:var(--text-disabled)]">
                        {notification.createdAt
                          ? formatRelativeTime(
                              notification.createdAt.toDate(),
                              locale,
                            )
                          : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
